import { type UseQueryResult, useQueries } from '@tanstack/react-query'

import {
  dataOrThrow,
  type GetMeResponse,
  type GetPatientSubscriptionResponse,
} from '@snootsvet/shared/portal'

import { usePortalApi } from '@/customer/hooks/use-api'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { petSubscriptionQueryKey } from '@/customer/hooks/use-get-pet-subscription'

export type PetWithSubscription = GetMeResponse['patients'][number] & {
  /**
   * Full query result rather than just data; consumers like
   * `PetSubscriptionGuard` need `isPending` for loading states and
   * pass-through query options like `refetchInterval` for polling.
   */
  subscriptionQuery: UseQueryResult<GetPatientSubscriptionResponse>
}

export interface PetsWithSubscriptionsResult {
  isPending: boolean
  isError: boolean
  pets: PetWithSubscription[]
}

/**
 * Aggregate view: every pet on `/me` joined with its live Stripe
 * subscription query. Fans out one `GET /pets/:id/subscription` per
 * pet through `useQueries`, sharing cache entries with any parallel
 * `useGetPetSubscription` calls elsewhere on the page.
 *
 * Pets with `hasSubscription === false` don't get fetched - there's
 * no linked Stripe id, so the server would return
 * `{ subscription: null }` anyway.
 * `initialData` shortcuts the query straight to that shape so
 * consumers see a resolved (non-pending) result immediately.
 *
 * Each pet's `subscription` is the raw React Query result, so
 * consumers see identical fields (`data`, `isPending`, `isError`,
 * `refetch`, ...) whether they called this or `useGetPetSubscription`
 * for a single pet. The top-level `isPending` / `isError` reflect
 * only the `/me` fetch - per-pet subscription state lives on each
 * `pet.subscriptionQuery`.
 */
export function usePetsWithSubscriptions(): PetsWithSubscriptionsResult {
  const api = usePortalApi()
  const me = useGetMe()
  const pets = me.data?.patients ?? []

  const subscriptions = useQueries({
    queries: pets.map((pet) => ({
      queryKey: petSubscriptionQueryKey(pet.id),
      queryFn: async () =>
        dataOrThrow(await api.get('/pets/:id/subscription', { params: { id: pet.id } })),
      enabled: pet.hasSubscription,
      ...(pet.hasSubscription
        ? {}
        : {
            initialData: {
              subscription: null,
              matchesMemberStripeCustomerId: false,
              incompletePayment: null,
            } satisfies GetPatientSubscriptionResponse,
          }),
    })),
  })

  if (me.isPending || me.isError) {
    return { isPending: me.isPending, isError: me.isError, pets: [] }
  }

  const enrichedPets: PetWithSubscription[] = pets.map((pet, i) => {
    const sub = subscriptions[i]
    return {
      ...pet,
      subscriptionQuery: sub,
    }
  })

  return { isPending: false, isError: false, pets: enrichedPets }
}
